import { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceEditTimesheetRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { durationToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import NoteField from 'modules/inbox_tasks/components/NoteField'
import {
  calculateDuration,
  locationTypeText,
} from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/helpers'
import { AttendanceEditTimesheetRequestTaskContentTypeProps } from 'modules/inbox_tasks/tasks/approval/AttendanceEditTimesheetRequest/types'

const CreateShiftRequestContent: FC<AttendanceEditTimesheetRequestTaskContentTypeProps> = ({
  id,
  requesterContent,
  moreDetails,
  workedTime,
  affecteeName,
}) => {
  const { data } = useFragment({
    fragment: AttendanceEditTimesheetRequestApprovableFragmentDoc,
    fragmentName: 'AttendanceEditTimesheetRequestApprovable',
    from: {
      __typename: 'AttendanceEditTimesheetRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const date = data.attendanceShiftDate
    ? stringToDate(data.attendanceShiftDate, 'backend_datetime')
    : undefined
  const displayDate = date ? i18nDateToString(date, 'ui_dmy_long') : ''

  const newClockIn = data.clockIn
  const newClockOut = data.clockOut
  const newLocationType = data.locationType
  const newObservations = data.observations
  const newIsBreak = !data.workable
  const newBreakName = data.timeSettingsBreakConfiguration?.name
  const newBreakPaid = data.timeSettingsBreakConfiguration?.paid

  const newDuration =
    newClockIn && newClockOut
      ? calculateDuration(newClockIn, newClockOut)
      : { hours: 0, minutes: 0 }

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent,
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.date'),
      content: {
        text: displayDate,
        type: 'item' as const,
      },
    },
    {
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.request_type'),
      content: {
        text: i18n.t(
          'tasks.approvals.attendance_edit_timesheet_request.fields.request_type_create'
        ),
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  // Add new shift details
  if (newClockIn) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_in'),
      content: {
        text: newClockIn,
        type: 'item' as const,
      },
    })
  }

  if (newClockOut) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.clock_out'),
      content: {
        text: newClockOut,
        type: 'item' as const,
      },
    })
  }

  if (newLocationType) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.location_type'),
      content: {
        text: locationTypeText(newLocationType),
        type: 'item' as const,
      },
    })
  }

  if (newObservations) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.observations'),
      content: {
        text: newObservations,
        type: 'item' as const,
      },
    })
  }

  // Add duration
  if (newDuration.hours > 0 || newDuration.minutes > 0) {
    const newDurationText = durationToText(newDuration.hours * 60 + newDuration.minutes, 'minutes')

    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.duration'),
      content: {
        text: newDurationText,
        type: 'item' as const,
      },
    })
  }

  // Add break information
  if (newIsBreak || newBreakName) {
    const newBreakText = newIsBreak
      ? newBreakName || i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.break')
      : i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.work')

    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.activity_type'),
      content: {
        text: newBreakText,
        type: 'item' as const,
      },
    })
  }

  if (newBreakPaid !== undefined) {
    details.push({
      title: i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.paid_break'),
      content: {
        text: newBreakPaid ? i18n.t('common.yes') : i18n.t('common.no'),
        type: 'item' as const,
      },
    })
  }

  const summary = `${
    date && affecteeName
      ? i18n.t('dashboard.todo.approvals.attendance.new_shift', {
          employee: affecteeName,
          date: i18nDateToString(date, 'ui_abbreviated_month_year'),
          duration_hours: newDuration.hours,
          duration_minutes: newDuration.minutes,
        })
      : ''
  }${
    workedTime
      ? '\n' + i18n.t('dashboard.todo.approvals.attendance.worked_time', { workedTime }) + '.'
      : ''
  }`

  return (
    <Box width="full" gap="s16">
      <DetailsItemsList details={details} tableView />
      <Box paddingBottom="s4">
        <F0Link href={moreDetails.url}>{moreDetails.title}</F0Link>
      </Box>
      {!!data.reason && (
        <NoteField
          label={i18n.t('tasks.approvals.attendance_edit_timesheet_request.fields.reason')}
          content={data.reason}
        />
      )}
      {!!summary && <NoteField content={summary} />}
    </Box>
  )
}

export default CreateShiftRequestContent
